import { Card } from "@/components/ui/card";
import {
  KpiTile,
  KpiLoadingState,
  LoadingState,
  ErrorState,
  EmptyState,
  AttentionBadge,
  hitRatePct,
  roundMiss,
  humanise,
} from "@/components/research-hub";
import { useCioCalibration } from "@/hooks/useResearchHubCio";

type Row = Record<string, any>;

// Series that clearly trace back to a real, document-sourced Bank of England forecast
// get a quiet "Bank of England" badge — our first batch of genuine scored calls.
function isBankOfEngland(series?: string, label?: string): boolean {
  const hay = `${series ?? ""} ${label ?? ""}`.toLowerCase();
  return hay.includes("boe") || hay.includes("bank of england");
}

export default function CioCalibration() {
  const { data, isLoading, error, refetch } = useCioCalibration();

  if (isLoading)
    return (
      <div className="space-y-8">
        <h1 className="font-heading text-2xl font-semibold">Our track record</h1>
        <KpiLoadingState tiles={3} />
        <LoadingState rows={4} />
      </div>
    );

  if (error) return <ErrorState onRetry={() => refetch()} />;

  // The RPC can answer { populated: false, note } when nothing has been scored,
  // or an { error } envelope — both resolve to the same calm empty state.
  if (!data || (data as Row).error || data.populated === false) {
    const note =
      typeof data?.note === "string" && data.note.trim()
        ? data.note
        : "No calls have been scored yet.";
    return (
      <div className="space-y-6">
        <h1 className="font-heading text-2xl font-semibold">Our track record</h1>
        <EmptyState title={note} />
      </div>
    );
  }

  const hr = hitRatePct(data.hit_rate);
  const mae = roundMiss(data.mean_abs_error);
  const nEvaluated = data.n_evaluated ?? 0;
  const nPredictions = data.n_predictions ?? 0;

  const perSeries: CalRow[] = Array.isArray(data.per_series)
    ? (data.per_series as CalRow[]).filter(Boolean)
    : [];

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h1 className="font-heading text-2xl font-semibold">Our track record</h1>
        <p className="font-body text-sm text-muted-foreground max-w-2xl">
          This is our forecasting track record. It does not yet feed our conviction.
        </p>
      </div>

      {/* Top scorecard */}
      <div className="flex flex-wrap gap-3">
        <KpiTile
          value={hr != null ? `${hr}%` : "—"}
          caption={hr != null ? "right about this often of the time" : "not yet scored"}
          tone="good"
        />
        <KpiTile value={nEvaluated} caption={`calls scored of ${nPredictions}`} />
        <KpiTile value={mae != null ? `~${mae}` : "—"} caption="typical miss" />
      </div>

      {/* Per-series league table, built dynamically from per_series[] */}
      <section className="space-y-3">
        <h2 className="font-heading text-lg font-medium text-foreground">By metric</h2>
        {perSeries.length === 0 ? (
          <EmptyState title="No individual metrics have been scored yet." />
        ) : (
          <div className="space-y-2">
            {perSeries.map((s, i) => (
              <SeriesRow key={s.series ?? s.series_label ?? i} s={s} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

interface CalRow extends Row {
  series?: string;
  series_label?: string;
  metric?: string;
  n_evaluated?: number;
  hit_rate?: number;
  mean_abs_error?: number;
}

function SeriesRow({ s }: { s: CalRow }) {
  const name = s.series_label ?? s.metric ?? null;
  const code = s.series ?? "";
  const n = s.n_evaluated ?? 0;
  const hr = hitRatePct(s.hit_rate);
  const boe = isBankOfEngland(s.series, s.series_label);

  return (
    <Card className="p-3 flex items-center justify-between gap-3">
      <div className="min-w-0 flex items-center gap-2 flex-wrap">
        {name ? (
          <span className="font-body text-sm truncate">{name}</span>
        ) : (
          <span className="font-body text-sm text-muted-foreground truncate">{humanise(code) || "A scored metric"}</span>
        )}
        {boe && <AttentionBadge label="Bank of England" tone="neutral" />}
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <span className="font-body text-xs text-muted-foreground">{n} scored</span>
        {n === 0 ? (
          <span className="font-body text-sm text-muted-foreground">not yet scored</span>
        ) : (
          <span className="font-body text-sm">
            <span className="text-muted-foreground">right </span>
            <span className="font-medium text-primary">{hr != null ? `${hr}%` : "—"}</span>
          </span>
        )}
      </div>
    </Card>
  );
}
